<template>
    <div v-if="loaded">
        <div v-if="dataElementPaths.length">
            <table class="table table-striped" >
                <thead>
                    <tr>
                        <th colspan="5"> Data Elements in <em>{{distribution.name}}</em> </th>
                    </tr>
                    <tr class="sticky-header">
                        <th>Path name</th>
                        <th>Data Element</th>
                        <th>Definition</th>
                        <th>Data Type</th>
                        <th>Physical Data Type</th>
                        <th>Format</th>
                    </tr>

                </thead>
                <tbody>
                    <tr v-for="dePath in ordered(dataElementPaths)" :key="dePath.uuid" >
                        <td>
                            <tt>
                                {{ dePath.logicalPath }}
                            </tt>
                        </td>
                        <td>
                            <a v-if="dePath.deId" :href="objectUrl + dePath.deId"
                                target="_blank" title='Open data element in new tab' >
                                {{ dePath.deName }}
                            </a>

                            <em v-else>
                                {{ dePath.deName }}
                            </em>
                        </td>
                        <td>
                            <!-- This definition is bleached on entry to dataset.
                                 The bleach template tag performs the same operation as is undertaken on dataset entry.
                                 v-html is usually unsafe to use, but for this case it can be used.
                                 TODO: look into better ways to handle v-html -->
                            <p v-html="dePath.deDefinition"/>

                            <div v-if="dePath.specificInfo">
                                <hr />
                                <strong>Specific Information:</strong>
                                <p v-html="dePath.specificInfo"></p>
                            </div>
                        </td>
                        <td>
                            {{ dePath.dataType }}
                        </td>
                        <td>
                            {{ dePath.physicalDataType }}
                        </td>
                        <td>
                            {{ dePath.format }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <strong v-else> No data elements have been specified for this distribution </strong>
    </div>
    <Loading v-else/>
</template>

<script>
import Loading from 'src/components/Loading.vue'
import apiRequest from 'src/mixins/api-request'
import constants from "src/components/editor/constants.js"
import { reverse } from 'src/lib/data/urls'

export default {
    name: "DistributionDataElementPath",
    components: {
        Loading
    },
    mixins: [apiRequest],
    props: {
        // The distribution which contains the paths which are rendered by this component
        distribution: {
            type: Object,
            required: true,
        }
    },
    data: () => ({
        loaded: false,
        dataElementPaths: [],
        objectUrl: '/item/'
    }),
    created: async function() {
        await this.load()
        this.loaded = true
    },
    methods: {
        load: async function() {
            try {
                const res = await this.get(reverse("editor_distribution", [this.distribution.id]))
                this.dataElementPaths = await Promise.all(res.data.distributiondataelementpath_set.map(this.loadDataElementInfo))
            } catch (err) {
                this.permission = false
                // TODO check permission with editor API?
            }
        },
        loadDataElementInfo: async function(path) {
            if (path.data_element) {
                // path points to a data element
                try {
                    const res = await this.get(reverse("metadata_dataelement", [path.data_element]))

                    // loaded successfully
                    return {
                        uuid: path.id,
                        order: path.order,
                        deId: path.data_element,
                        logicalPath: path.logical_path === '' ? '-' : path.logical_path,
                        specificInfo: path.specific_information,
                        dataType: path.data_type,
                        physicalDataType: path.physical_data_type_name,
                        format: path.format,
                        deName: res.data.name,
                        deDefinition: res.data.definition === "" ? "-" : res.data.definition
                    }
                } catch (err) {
                    // couldn't fetch data element, hide fields
                    const empty = {
                        uuid: path.id,
                        order: path.order,
                        deId: path.data_element,
                        logicalPath: "-",
                        specificInfo: null,
                        dataType: "-",
                        physicalDataType: "-",
                        format: "-",
                        deDefinition: "-",
                    }
                    if (err.response && (err.response.status === constants.ErrorCodes.unauthorised || err.response.status === constants.ErrorCodes.forbidden)) {
                        // permissions error
                        empty.deName = "You do not have permission to view this data element"
                    } else {
                        // some other error
                        empty.deName = `Encountered error while fetching data element: ${err.message}`
                    }
                    return empty
                }
            } else {
                // path doesn't point to a data element
                return {
                    uuid: path.id,
                    order: path.order,
                    deId: path.data_element,
                    logicalPath: path.logical_path === '' ? '-' : path.logical_path,
                    specificInfo: path.specific_information,
                    dataType: path.data_type,
                    physicalDataType: path.physical_data_type_name,
                    format: path.format,
                    deName: "-",
                    deDefinition: "-"
                }
            }
        },
        ordered: function(list) {
            return list.toSorted((a, b) => a.order - b.order);
        }
    }

}
</script>

<style scoped>
table {
    border-collapse: separate;
    table-layout: fixed;
    width: 100%;
    overflow-wrap: break-word;
}

thead {
    position: sticky;
    top: 0;
    background: whitesmoke;
}
</style>
